import { Injectable } from '@angular/core';
import { AssetListReleaseDateState } from '../interfaces/asset-list-actions.interfaces';
import { MONTHS, UNKNOWN_MONTH_MESSAGE, SVG_ICON_PATHS } from '../constants/asset-list-actions.constants';

@Injectable({
  providedIn: 'root',
})
export class AssetListReleaseDateService {
  private readonly months: string[] = MONTHS;

  public updateReleaseDateOptions(assets: any[]): AssetListReleaseDateState {
    const state: AssetListReleaseDateState = {
      availableYears: [],
      availableMonthsPerYear: {},
      allAssetReleaseDates: [],
      releaseMonthYear: {},
      monthCollapse: {},
      yearSelected: [],
      years: [],
      months: this.months,
      allReleaseSelected: false,
    };

    if (!assets || assets.length === 0) {
      return state;
    }

    assets.forEach((asset: any) => {
      if (asset.releaseDate) {
        let timestamp: number;

        if (typeof asset.releaseDate === 'string') {
          timestamp = Number.parseInt(asset.releaseDate, 10);
        } else {
          timestamp = asset.releaseDate;
        }

        if (!Number.isNaN(timestamp)) {
          state.allAssetReleaseDates.push(timestamp);
        }
      }
    });

    const yearMonthMap: Map<number, Set<string>> = new Map<number, Set<string>>();

    state.allAssetReleaseDates.forEach((timestamp: number) => {
      try {
        const date: Date = new Date(timestamp);

        if (Number.isNaN(date.getTime())) return;

        const year: number = date.getFullYear();
        const month: string = String(date.getMonth() + 1).padStart(2, '0');

        if (!yearMonthMap.has(year)) {
          yearMonthMap.set(year, new Set<string>());
        }
        yearMonthMap.get(year)?.add(month);
      } catch {
        // Error processing timestamp - skip invalid dates
      }
    });

    state.availableYears = Array.from(yearMonthMap.keys()).sort((a: number, b: number) => b - a);
    state.years = [...state.availableYears];

    yearMonthMap.forEach((months: Set<string>, year: number) => {
      state.availableMonthsPerYear[year] = Array.from(months).sort((a: string, b: string) => a.localeCompare(b));
    });

    state.availableYears.forEach((year: number) => {
      state.monthCollapse[year] = false;
      state.releaseMonthYear[year] = {};

      this.months.forEach((month: string) => {
        state.releaseMonthYear[year][month] = false;
      });
    });

    if (state.availableYears.length > 0) {
      const [firstYear] = state.availableYears;
      state.monthCollapse[firstYear] = true;
    }

    return state;
  }

  public selectYear(
    year: number,
    state: AssetListReleaseDateState,
    onUpdate: (selectedValues: string[]) => void
  ): AssetListReleaseDateState {
    const newState: AssetListReleaseDateState = { ...state };

    if (newState.yearSelected.includes(year)) {
      newState.yearSelected = newState.yearSelected.filter((y: number) => y !== year);
      this.months.forEach((month: string) => {
        newState.releaseMonthYear[year][month] = false;
      });
    } else {
      newState.yearSelected = [...newState.yearSelected, year];
      this.months.forEach((month: string) => {
        newState.releaseMonthYear[year][month] = true;
      });
    }

    const selectedValues: string[] = this.generateReleaseDateValues(newState);
    onUpdate(selectedValues);

    return newState;
  }

  public monthSelected(
    year: number,
    month: string,
    state: AssetListReleaseDateState,
    onUpdate: (selectedValues: string[]) => void
  ): AssetListReleaseDateState {
    const newState: AssetListReleaseDateState = { ...state };

    if (!newState.releaseMonthYear[year]) {
      newState.releaseMonthYear[year] = {};
      this.months.forEach((m: string) => {
        newState.releaseMonthYear[year][m] = false;
      });
    }

    newState.releaseMonthYear[year][month] = !newState.releaseMonthYear[year][month];

    const availableMonths: string[] = newState.availableMonthsPerYear[year] || [];

    if (availableMonths.length > 0) {
      const allAvailableMonthsSelected: boolean = availableMonths.every(
        (m: string) => newState.releaseMonthYear[year][m]
      );
      const noAvailableMonthsSelected: boolean = availableMonths.every(
        (m: string) => !newState.releaseMonthYear[year][m]
      );

      if (noAvailableMonthsSelected) {
        newState.yearSelected = newState.yearSelected.filter((y: number) => y !== year);
      } else if (allAvailableMonthsSelected && !newState.yearSelected.includes(year)) {
        newState.yearSelected.push(year);
      } else if (!allAvailableMonthsSelected) {
        newState.yearSelected = newState.yearSelected.filter((y: number) => y !== year);
      }
    }

    const selectedValues: string[] = this.generateReleaseDateValues(newState);
    onUpdate(selectedValues);

    return newState;
  }

  public toggleAllReleaseDates(
    state: AssetListReleaseDateState,
    onUpdate: (selectedValues: string[]) => void
  ): AssetListReleaseDateState {
    const newState: AssetListReleaseDateState = { ...state };
    newState.allReleaseSelected = !newState.allReleaseSelected;

    if (newState.allReleaseSelected) {
      newState.yearSelected = [...newState.years];
      newState.years.forEach((year: number) => {
        this.months.forEach((month: string) => {
          newState.releaseMonthYear[year][month] = true;
        });
      });
      const selectedValues: string[] = newState.yearSelected.flatMap((y: number) =>
        this.months.map((m: string) => `${y}-${m}`)
      );
      onUpdate(selectedValues);
    } else {
      newState.yearSelected = [];
      newState.years.forEach((year: number) => {
        this.months.forEach((month: string) => {
          newState.releaseMonthYear[year][month] = false;
        });
      });
      onUpdate([]);
    }

    return newState;
  }

  public generateReleaseDateValues(state: AssetListReleaseDateState): string[] {
    const selectedValues: string[] = [];

    state.availableYears.forEach((year: number) => {
      if (!state.releaseMonthYear[year]) {
        return;
      }

      if (state.yearSelected.includes(year)) {
        state.availableMonthsPerYear[year].forEach((month: string) => {
          selectedValues.push(`${year}-${month}`);
        });
      } else {
        state.availableMonthsPerYear[year].forEach((month: string) => {
          if (state.releaseMonthYear[year][month]) {
            selectedValues.push(`${year}-${month}`);
          }
        });
      }
    });

    return selectedValues;
  }

  public preSelectReleaseDates(selectedReleaseDateValues: string[], state: AssetListReleaseDateState): AssetListReleaseDateState {
    const newState: AssetListReleaseDateState = { ...state };

    if (selectedReleaseDateValues?.length) {
      selectedReleaseDateValues.forEach((date: string) => {
        const parts: string[] = date.split('-');
        if (parts.length === 2) {
          const year: number = Number.parseInt(parts[0], 10);
          const [, month] = parts;

          if (newState.releaseMonthYear[year]) {
            newState.releaseMonthYear[year][month] = true;

            const allMonthsSelected: boolean = this.months.every(
              (m: string) => !newState.availableMonthsPerYear[year]?.includes(m) || newState.releaseMonthYear[year][m]
            );

            if (allMonthsSelected && !newState.yearSelected.includes(year)) {
              newState.yearSelected.push(year);
            }
          }
        }
      });
    }

    return newState;
  }

  public getMonthName(monthNum: string): string {
    const monthIndex: number = Number.parseInt(monthNum, 10) - 1;

    if (Number.isNaN(monthIndex) || monthIndex < 0 || monthIndex > 11) {
      return UNKNOWN_MONTH_MESSAGE;
    }

    const date: Date = new Date(2000, monthIndex, 1);
    return date.toLocaleString('default', { month: 'long' });
  }

  public getReleaseDateDropdownPreIcon(
    availableYears: number[],
    availableMonthsPerYear: { [year: number]: string[] },
    selectedReleaseDateValues: string[]
  ): string {
    let totalAvailableDates: number = 0;
    for (const year of availableYears) {
      if (availableMonthsPerYear[year]) {
        totalAvailableDates += availableMonthsPerYear[year].length;
      }
    }

    if (
      selectedReleaseDateValues &&
      totalAvailableDates > 0 &&
      selectedReleaseDateValues.length === totalAvailableDates
    ) {
      return SVG_ICON_PATHS.SELECTED;
    }

    return selectedReleaseDateValues?.length ? SVG_ICON_PATHS.FILTERED : SVG_ICON_PATHS.UNSELECT;
  }
}
